import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getPostsSettings, manageCommentsSettings } from 'services/actions/postsService';
import Modal from 'components/actions/ActionSettingsModal';
import { toast } from 'react-toastify';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';

interface PostsSettingsProps {
  selectedUser: string;
}

interface Setting {
  _id: string;
  action_id: string;
  settings_type: string;
  description: string;
  settings_value: any;
  is_active?: boolean;
  category_enabled?: boolean;
  name?: string;
  friendly_name?: string;
}

interface SettingsCategory {
  title: string;
  options: Setting[];
}

const PostsSettings: React.FC<PostsSettingsProps> = ({ selectedUser }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingKeyword, setEditingKeyword] = useState<{ index: number; optionIndex: number }>({ index: -1, optionIndex: -1 });
  const [settingsType, setSettingsType] = useState<string>('');
  const [settingsSubType, setSettingsSubType] = useState<string>('');
  const [oldKeywordName, setOldKeywordName] = useState<string>('');
  const [oldHeader, setOldHeader] = useState<string>('');
  const [oldValue, setOldValue] = useState<string>('');
  const [actionID, setActionID] = useState<string>('');

  const [isModalOpen, setModalOpen] = useState<boolean>(false);

  // Theme tokens
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const sectionHeaderColor = isDark ? '#E2E8F0' : '#314158';
  const sectionBorder = isDark ? '#1D293D' : '#F1F5F9';
  const labelColor = isDark ? '#E2E8F0' : '#314158';
  const descriptionColor = isDark ? '#90A1B9' : '#62748E';
  const switchOnBg = isDark ? '#51A2FF' : '#2B7FFF';
  const switchKnob = isDark ? '#0F172B' : '#FFFFFF';
  const switchOffBg = isDark ? '#314158' : '#E2E8F0';
  const inputBg = isDark ? '#0F172B' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputText = isDark ? '#E2E8F0' : '#314158';
  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const infoBg = isDark ? '#162456' : '#EFF6FF';
  const infoBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const infoText = isDark ? '#93C5FD' : '#1E40AF';
  const warningBg = isDark ? '#422006' : '#FFFBEB';
  const warningBorder = isDark ? '#92400E' : '#FDE68A';
  const warningText = isDark ? '#FCD34D' : '#92400E';
  const buttonGradient = 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)';
  const disabledLabelColor = isDark ? '#62748E' : '#90A1B9';
  const iconButtonBg = isDark ? '#162456' : '#EFF6FF';
  const iconButtonColor = isDark ? '#51A2FF' : '#2B7FFF';
  const dangerBg = isDark ? '#3B0A0A' : '#FEF2F2';
  const dangerColor = isDark ? '#FF637E' : '#EF4444';
  const itemBg = isDark ? '#1D293D' : '#F8FAFC';
  const itemBorder = isDark ? '#314158' : '#E2E8F0';
  const itemText = isDark ? '#E2E8F0' : '#314158';
  const editBtnBg = isDark ? '#1D293D' : '#F8FAFC';
  const editBtnText = isDark ? '#CAD5E2' : '#45556C';
  const checkboxAccent = isDark ? '#51A2FF' : '#2B7FFF';

  const closeModal = () => setModalOpen(false);

  const saveSettings = async (data: any) => {
    try {
      let result: any;
      switch (settingsType) {
        case "toggle":
          result = await manageCommentsSettings(selectedUser, actionID, settingsType, "edit", data);
          fetchSettings();
          return "General";

        case "keyword_list":
          switch (settingsSubType) {
            case "add":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, [data.keywordName]);
              fetchSettings();
              return "Adding Keyword";
            case "edit":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { old: data.oldKeywordName, new: data.keywordName });
              fetchSettings();
              return "Editing Keyword";
            case "remove":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType,data);
              fetchSettings();
              return "Deleting Keyword";
            default:
              return "Unknown Keyword Action";
          }

        case "template_list":
          switch (settingsSubType) {
            case "add":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, [{ template_name: data.header, template_data: data.value }]);
              fetchSettings();
              return "Adding Template";
            case "edit":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType,{ template_name: data.oldHeader, template_data: data.value });
              fetchSettings();
              return "Editing Template";
            case "remove":
              if ((settingsCategories.find(category => category.title === "template_list")?.options?.length ?? 0) === 1) {
                toast.error(t('postsSettings.errors.cannotDeleteDefaultTemplate'));
                return "Deleting Template";
              }
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: data.oldHeader });
              fetchSettings();
              return "Deleting Template";
            default:
              return "Unknown Template Action";
          }

        case "text_list":
          switch (settingsSubType) {
            case "add":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, [{ template_name: data.header, template_data: data.value }]);
              fetchSettings();
              return "Adding Template";
            case "edit":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: data.oldHeader, template_data: data.value });
              fetchSettings();
              return "Editing Template";
            case "remove":
              if ((settingsCategories.find(category => category.title === "text_list")?.options?.length ?? 0) === 1) {
                toast.error(t('postsSettings.errors.cannotDeleteDefaultTemplate'));
                return "Deleting Template";
              }
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: data.oldHeader });
              fetchSettings();
              return "Deleting Template";
            default:
              return "Unknown Template Action";
          }

        case "repost_sub_menu":
          switch (settingsSubType) {
            case "toggle_active":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, data);
              fetchSettings();
              return "Toggle Repost Active";
            case "toggle_setting":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, {
                setting_name: data.settingName,
                value: data.value
              });
              fetchSettings();
              return "Toggle Repost Setting";
            default:
              return "Unknown Repost Action";
          }

        default:
          return "Unknown Settings Type";
      }
    } catch (err: any) {
      console.error("Error saving settings:", err);
      setError(err.message);
    }
  };

  const fetchSettings = async () => {
    try {
      if (!selectedUser) throw new Error("No user selected");
      const response: any = await getPostsSettings(selectedUser);
      setSettings(response || []);
      setLoading(false);
    } catch (err: any) {
      console.error("Error fetching settings:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUser) {
      fetchSettings();
    }
  }, [selectedUser]);

  const toggleSetting = async (actionId: string, category: string, option: Setting, newValue: boolean) => {
    try {
      setSettings(prev => prev.map(item =>
        item.action_id === actionId && item.settings_type === category
          ? { ...item, settings_value: newValue }
          : item
      ));

      await manageCommentsSettings(selectedUser, actionId, category, "edit", newValue);
    } catch (err: any) {
      console.error("Error toggling setting:", err);
      setError(err.message);
      setSettings(prev => prev.map(item =>
        item.action_id === actionId && item.settings_type === category
          ? { ...item, settings_value: !newValue }
          : item
      ));
    }
  };

  const toggleRepostActive = async (actionId: string, newValue: boolean) => {
    try {
      setSettings(prev => prev.map(item =>
        item.action_id === actionId
          ? { ...item, is_active: newValue }
          : item
      ));

      await manageCommentsSettings(selectedUser, actionId, "repost_sub_menu", "toggle_active", newValue);
    } catch (err: any) {
      console.error("Error toggling repost active:", err);
      setError(err.message);
      setSettings(prev => prev.map(item =>
        item.action_id === actionId
          ? { ...item, is_active: !newValue }
          : item
      ));
    }
  };

  const toggleRepostSetting = async (actionId: string, settingName: string, newValue: string) => {
    try {
      setSettings(prev => prev.map(item => {
        if (item.action_id === actionId) {
          const updatedSettings = item.settings_value.map((setting: any) =>
            setting.name === settingName
              ? { ...setting, value: newValue }
              : setting
          );
          return { ...item, settings_value: updatedSettings };
        }
        return item;
      }));

      await manageCommentsSettings(selectedUser, actionId, "repost_sub_menu", "toggle_setting", {
        setting_name: settingName,
        value: newValue
      });
    } catch (err: any) {
      console.error("Error toggling repost setting:", err);
      setError(err.message);
      // Revert on error
      setSettings(prev => prev.map(item => {
        if (item.action_id === actionId) {
          const updatedSettings = item.settings_value.map((setting: any) =>
            setting.name === settingName
              ? { ...setting, value: newValue === "true" ? "false" : "true" }
              : setting
          );
          return { ...item, settings_value: updatedSettings };
        }
        return item;
      }));
    }
  };

  const handleAddKeyword = (optionIndex: number) => {
    setSettings(prev => prev.map((item, idx) =>
      idx === optionIndex
        ? { ...item, settings_value: [...item.settings_value, ""] }
        : item
    ));
  };

  const handleKeywordChange = (optionIndex: number, keyIndex: number, value: string) => {
    setSettings(prev => prev.map((item, idx) => {
      if (idx === optionIndex) {
        const newValues = [...item.settings_value];
        newValues[keyIndex] = value;
        return { ...item, settings_value: newValues };
      }
      return item;
    }));
  };

  const handleTemplateChange = (optionIndex: number, templateIndex: number, field: string, value: string) => {
    setSettings(prev => prev.map((item, idx) => {
      if (idx === optionIndex) {
        const newTemplates = item.settings_value.map((tpl: any, tplIdx: number) =>
          tplIdx === templateIndex ? { ...tpl, [field]: value } : tpl
        );
        return { ...item, settings_value: newTemplates };
      }
      return item;
    }));
  };

  if (loading) {
   return <Spinner size="large" text={t('postsSettings.loading')} fullPage />;
  }

  if (error) {
    return (
      <div style={{
        background: mainBg,
        borderRadius: 12,
        padding: 32,
        maxWidth: 400,
        margin: '32px auto',
        textAlign: 'center',
        border: `1px solid ${cardBorder}`
      }}>
        <div style={{
          width: 48,
          height: 48,
          background: dangerBg,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px'
        }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 6v4m0 4h.01M3.072 17h13.856c1.078 0 1.756-1.164 1.22-2.094L11.22 3.094a1.4 1.4 0 0 0-2.44 0L1.852 14.906C1.316 15.836 1.994 17 3.072 17z" stroke={dangerColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: sectionHeaderColor, marginBottom: 8 }}>{t('postsSettings.errors.loadingError')}</h3>
        <p style={{ fontSize: 12, color: descriptionColor, marginBottom: 16 }}>{error}</p>
        <button
          style={{
            padding: '10px 20px',
            background: buttonGradient,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600
          }}
          onClick={() => window.location.reload()}
        >
          {t('postsSettings.buttons.retry')}
        </button>
      </div>
    );
  }

  const settingsCategories: SettingsCategory[] = [...new Set(settings.map(item => item.settings_type))].map(settingsType => {
    const filteredSettings = settings.filter(item => item.settings_type === settingsType);
    return {
      title: settingsType,
      options: filteredSettings
    };
  });

  const SwitchToggle: React.FC<{
    checked: boolean;
    disabled?: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }> = ({ checked, disabled, onChange }) => (
    <label style={{
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1
    }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
      />
      <div style={{
        width: 40,
        height: 20,
        borderRadius: 999,
        background: checked ? switchOnBg : switchOffBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: checked ? 'flex-end' : 'flex-start',
        padding: 2,
        transition: 'background 0.2s ease'
      }}>
        <div style={{
          width: 16,
          height: 16,
          borderRadius: 999,
          background: switchKnob,
          transition: 'all 0.2s ease'
        }} />
      </div>
    </label>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSave={saveSettings}
          selectedSettingsType={settingsType}
          selectedSettingsSubType={settingsSubType}
          selectedOldKeywordName={oldKeywordName}
          selectedOldHeader={oldHeader}
          selectedOldValue={oldValue}
        />
      )}

      {settingsCategories.map((category, index) => (
        <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Section header */}
          <div style={{
            borderBottom: `1px solid ${sectionBorder}`,
            paddingBottom: 16
          }}>
            <h3 style={{
              fontSize: 16,
              fontWeight: 700,
              color: sectionHeaderColor,
              margin: 0
            }}>
              {{
                toggle: t('postsSettings.sections.generalSettings'),
                keyword_list: t('postsSettings.sections.keywords'),
                template_list: t('postsSettings.sections.templates'),
                text_list: t('postsSettings.sections.textLists'),
                repost_sub_menu: t('postsSettings.sections.repostSettings')
              }[category.title] || category.title}
            </h3>
          </div>

          {category.title === "toggle" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.some(opt => opt.category_enabled === false) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: 12,
                  background: warningBg,
                  border: `1px solid ${warningBorder}`,
                  borderRadius: 8
                }}>
                  <p style={{ fontSize: 12, color: warningText, margin: 0 }}>{t('postsSettings.warnings.categoryDisabled')}</p>
                </div>
              )}
              {category.options.map((option, optIndex) => {
                const isReadOnly = option.category_enabled === false;
                return (
                  <div key={optIndex} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <span style={{
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: 500,
                        color: isReadOnly ? disabledLabelColor : labelColor
                      }}>{option.description}</span>
                      <span style={{
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: 400,
                        color: descriptionColor
                      }}>{t('postsSettings.descriptions.toggleSetting', 'Toggle this setting on or off.')}</span>
                    </div>
                    <SwitchToggle
                      checked={option.settings_value || false}
                      disabled={isReadOnly}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (isReadOnly) return;
                        const newValue = e.target.checked;
                        setActionID(option.action_id);
                        setSettingsType("toggle");
                        setSettingsSubType("edit");
                        toggleSetting(option.action_id, "toggle", option, newValue);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {category.title === "keyword_list" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.map((option, optIndex) => (
                <div key={optIndex}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12
                  }}>
                    <span style={{ fontSize: 12, color: labelColor, fontWeight: 500 }}>{option.description}</span>
                    <button
                      style={{
                        width: 32,
                        height: 32,
                        background: iconButtonBg,
                        color: iconButtonColor,
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        fontWeight: 700
                      }}
                      onClick={() => {
                        setSettingsType('keyword_list');
                        setSettingsSubType('add');
                        setModalOpen(true);
                        setActionID(option.action_id);
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {option.settings_value.map((keyword: string, keyIndex: number) => (
                      <div key={keyIndex} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: 12,
                        background: itemBg,
                        borderRadius: 8,
                        border: `1px solid ${itemBorder}`
                      }}>
                        <div style={{ flex: 1 }}>
                          {editingKeyword.optionIndex === optIndex && editingKeyword.index === keyIndex ? (
                            <input
                              type="text"
                              value={keyword}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleKeywordChange(
                                settings.findIndex(s => s._id === option._id),
                                keyIndex,
                                e.target.value
                              )}
                              onBlur={() => setEditingKeyword({ index: -1, optionIndex: -1 })}
                              style={{
                                width: '100%',
                                padding: '4px 8px',
                                fontSize: 12,
                                background: inputBg,
                                border: `1px solid ${inputBorder}`,
                                borderRadius: 4,
                                color: inputText,
                                outline: 'none'
                              }}
                              autoFocus
                            />
                          ) : (
                            <span style={{ fontSize: 12, color: itemText }}>{keyword}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            style={{
                              width: 32,
                              height: 32,
                              background: iconButtonBg,
                              color: iconButtonColor,
                              border: 'none',
                              borderRadius: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onClick={() => {
                              setSettingsType('keyword_list');
                              setSettingsSubType('edit');
                              setModalOpen(true);
                              setOldKeywordName(keyword);
                              setActionID(option.action_id);
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                              <path d="M11.333 2a1.886 1.886 0 0 1 2.667 2.667L5.333 13.333 2 14l.667-3.333L11.333 2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          <button
                            style={{
                              width: 32,
                              height: 32,
                              background: dangerBg,
                              color: dangerColor,
                              border: 'none',
                              borderRadius: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onClick={() => {
                              setSettingsType('keyword_list');
                              setSettingsSubType('remove');
                              setModalOpen(true);
                              setOldKeywordName(keyword);
                              setActionID(option.action_id);
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                              <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {category.title === "template_list" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.map((option, optIndex) => (
                <div key={optIndex}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12
                  }}>
                    <span style={{ fontSize: 12, color: labelColor, fontWeight: 500 }}>{option.description}</span>
                    <button
                      style={{
                        width: 32,
                        height: 32,
                        background: iconButtonBg,
                        color: iconButtonColor,
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        fontWeight: 700
                      }}
                      onClick={() => {
                        setSettingsType('template_list');
                        setSettingsSubType('add');
                        setModalOpen(true);
                        setActionID(option.action_id);
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {option.settings_value.map((template: { template_name: string; template_data: string }, tplIndex: number) => (
                      <div key={tplIndex} style={{
                        background: cardBg,
                        borderRadius: 8,
                        border: `1px solid ${cardBorder}`,
                        overflow: 'hidden'
                      }}>
                        <div style={{ padding: 16 }}>
                          <p style={{
                            fontSize: 12,
                            color: itemText,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0
                          }}>
                            {template.template_data}
                          </p>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 16px',
                          borderTop: `1px solid ${cardBorder}`
                        }}>
                          <button
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              fontSize: 12,
                              color: editBtnText,
                              background: editBtnBg,
                              border: 'none',
                              borderRadius: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8
                            }}
                            onClick={() => {
                              setSettingsType('template_list');
                              setSettingsSubType('edit');
                              setModalOpen(true);
                              setOldHeader(template.template_name);
                              setOldValue(template.template_data);
                              setActionID(option.action_id);
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                              <path d="M11.333 2a1.886 1.886 0 0 1 2.667 2.667L5.333 13.333 2 14l.667-3.333L11.333 2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>Edit</span>
                          </button>
                          {(settingsCategories.find(category => category.title === "template_list")?.options?.length ?? 0) !== 1 && (
                            <button
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                fontSize: 12,
                                color: dangerColor,
                                background: dangerBg,
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8
                              }}
                              onClick={() => {
                                setSettingsType('template_list');
                                setSettingsSubType('remove');
                                setModalOpen(true);
                                setOldHeader(template.template_name);
                                setOldValue(template.template_data);
                                setActionID(option.action_id);
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span>Delete</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {category.title === "text_list" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.map((option, optIndex) => (
                <div key={optIndex}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12
                  }}>
                    <span style={{ fontSize: 12, color: labelColor, fontWeight: 500 }}>{option.description}</span>
                    <button
                      style={{
                        width: 32,
                        height: 32,
                        background: iconButtonBg,
                        color: iconButtonColor,
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        fontWeight: 700
                      }}
                      onClick={() => {
                        setSettingsType('text_list');
                        setSettingsSubType('add');
                        setModalOpen(true);
                        setActionID(option.action_id);
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {option.settings_value.map((template: { template_name: string; template_data: string }, tplIndex: number) => (
                      <div key={tplIndex} style={{
                        background: cardBg,
                        borderRadius: 8,
                        border: `1px solid ${cardBorder}`,
                        overflow: 'hidden'
                      }}>
                        <div style={{ padding: 16 }}>
                          <p style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: itemText,
                            marginBottom: 8,
                            marginTop: 0
                          }}>{template.template_name}</p>
                          <p style={{
                            fontSize: 12,
                            color: descriptionColor,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0
                          }}>
                            {template.template_data}
                          </p>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 16px',
                          borderTop: `1px solid ${cardBorder}`
                        }}>
                          <button
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              fontSize: 12,
                              color: editBtnText,
                              background: editBtnBg,
                              border: 'none',
                              borderRadius: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8
                            }}
                            onClick={() => {
                              setSettingsType('text_list');
                              setSettingsSubType('edit');
                              setModalOpen(true);
                              setOldHeader(template.template_name);
                              setOldValue(template.template_data);
                              setActionID(option.action_id);
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                              <path d="M11.333 2a1.886 1.886 0 0 1 2.667 2.667L5.333 13.333 2 14l.667-3.333L11.333 2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>Edit</span>
                          </button>
                          {(settingsCategories.find(category => category.title === "text_list")?.options?.length ?? 0) !== 1 && (
                            <button
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                fontSize: 12,
                                color: dangerColor,
                                background: dangerBg,
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8
                              }}
                              onClick={() => {
                                setSettingsType('text_list');
                                setSettingsSubType('remove');
                                setModalOpen(true);
                                setOldHeader(template.template_name);
                                setOldValue(template.template_data);
                                setActionID(option.action_id);
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span>Delete</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {category.title === "repost_sub_menu" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.map((option, optIndex) => (
                <div key={optIndex}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {option.settings_value.map((setting: any, settingIndex: number) => (
                      <div key={settingIndex} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: 12,
                        background: itemBg,
                        borderRadius: 8,
                        border: `1px solid ${itemBorder}`
                      }}>
                        <input
                          type="checkbox"
                          id={`repost-criteria-${optIndex}-${settingIndex}`}
                          checked={setting.value === "true"}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const newValue = e.target.checked ? "true" : "false";
                            setActionID(option.action_id);
                            setSettingsType('repost_sub_menu');
                            setSettingsSubType('toggle_setting');
                            toggleRepostSetting(option.action_id, setting.name, newValue);
                          }}
                          disabled={!option.is_active}
                          style={{
                            accentColor: checkboxAccent,
                            width: 16,
                            height: 16,
                            marginTop: 2,
                            cursor: !option.is_active ? 'not-allowed' : 'pointer',
                            flexShrink: 0
                          }}
                        />
                        <label
                          htmlFor={`repost-criteria-${optIndex}-${settingIndex}`}
                          style={{ cursor: !option.is_active ? 'not-allowed' : 'pointer', flex: 1 }}
                        >
                          <div style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: labelColor,
                            marginBottom: 4
                          }}>{setting.name}</div>
                          <div style={{
                            fontSize: 12,
                            color: descriptionColor
                          }}>{setting.description}</div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default PostsSettings;
